import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, Sparkles, Send, CalendarClock, Share2, ImagePlus, X, Globe,
} from "lucide-react";

const CHECK_ICON = { approved: CheckCircle2, warning: AlertTriangle, error: XCircle };
const CHECK_COLOR = { approved: "text-[#10B981]", warning: "text-[#F59E0B]", error: "text-[#EF4444]" };
const CHECK_LABEL = { technical: "Datos técnicos", seo: "SEO", format: "Formato", brand: "Marca", legal: "Legal" };
const PLATFORMS = ["facebook", "instagram", "twitter", "nextdoor"];
const PUSHABLE_STATUSES = ["approved", "scheduled", "published"];
const GMB_CTA_OPTIONS = ["Get a quote", "Call now", "Learn more"];
const WORDPRESS_TYPES = ["blog", "service_page", "location_page"];

export default function ContentDetailSheet({ item, open, onOpenChange, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [scheduleAt, setScheduleAt] = useState("");
  const [images, setImages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pushAccountIds, setPushAccountIds] = useState([]);
  const [wpConn, setWpConn] = useState(null);
  const [wpCategories, setWpCategories] = useState([]);
  const [wpCategoryId, setWpCategoryId] = useState("");
  const [wpTagsCsv, setWpTagsCsv] = useState("");
  const [wpNewCategoryName, setWpNewCategoryName] = useState("");
  const [wpCreatingCategory, setWpCreatingCategory] = useState(false);
  const [wpStatus, setWpStatus] = useState("draft");

  useEffect(() => {
    if (!open) return;
    api.get("/images").then((r) => setImages(r.data)).catch(() => {});
    api.get("/social/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    setPushAccountIds([]);
  }, [open]);

  useEffect(() => {
    if (!open || !item || !WORDPRESS_TYPES.includes(item.content_type)) return;
    setWpTagsCsv(item.location || "");
    setWpStatus("draft");
    api.get("/integrations/wordpress/connection").then((r) => setWpConn(r.data)).catch(() => setWpConn(null));
  }, [open, item]);

  useEffect(() => {
    if (!open || !wpConn || wpConn.connection_status !== "connected" || !item || !WORDPRESS_TYPES.includes(item.content_type)) return;
    api.get("/integrations/wordpress/categories").then((r) => {
      setWpCategories(r.data);
      const match = r.data.find((c) => c.name.toLowerCase() === (item.service || "").toLowerCase());
      if (match) setWpCategoryId(String(match.id));
    }).catch(() => setWpCategories([]));
  }, [open, wpConn, item]);

  if (!item) return null;

  const run = async (key, fn) => {
    setBusy(key);
    try {
      const res = await fn();
      onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo completar la acción");
    } finally {
      setBusy(null);
    }
  };

  const totalCost = (item.generation_logs || []).reduce((s, l) => s + (l.cost_usd || 0), 0);

  const attachImage = async (imageId) => {
    setBusy("attach-image");
    try {
      const res = await api.post(`/content/items/${item.id}/attach-image`, { generated_image_id: imageId });
      onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo asociar la imagen");
    } finally {
      setBusy(null);
    }
  };

  const togglePushAccount = (id) => {
    setPushAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setGmbCta = async (cta) => {
    setBusy("gmb-cta");
    try {
      const res = await api.post(`/content/items/${item.id}/gmb-cta`, { cta });
      onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el CTA");
    } finally {
      setBusy(null);
    }
  };

  const pushToPublications = async () => {
    if (pushAccountIds.length === 0) return toast.error("Elegí al menos una cuenta destino");
    setBusy("push");
    try {
      await api.post(`/content/items/${item.id}/push-to-publications`, { account_ids: pushAccountIds });
      toast.success("Enviado a Publicaciones como borrador");
      setPushAccountIds([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar a publicaciones");
    } finally {
      setBusy(null);
    }
  };

  const createWpCategory = async () => {
    if (!wpNewCategoryName.trim()) return;
    setWpCreatingCategory(true);
    try {
      const res = await api.post("/integrations/wordpress/categories", { name: wpNewCategoryName.trim() });
      setWpCategories((prev) => [...prev, res.data]);
      setWpCategoryId(String(res.data.id));
      setWpNewCategoryName("");
      toast.success("Categoría creada en WordPress");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la categoría");
    } finally {
      setWpCreatingCategory(false);
    }
  };

  const publishToWordPress = async () => {
    setBusy("wordpress");
    try {
      const res = await api.post(`/content/items/${item.id}/publish-to-wordpress`, {
        status: wpStatus,
        category_id: wpCategoryId ? Number(wpCategoryId) : null,
        tag_names: wpTagsCsv.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onChanged(res.data);
      toast.success("Publicado en WordPress");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo publicar en WordPress");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="content-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-left">{item.title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          {item.error_message && (
            <div className="bg-[#EF4444]/10 text-[#EF4444] text-sm p-3 rounded-md" data-testid="content-error-message">
              Falló la generación: {item.error_message}
            </div>
          )}

          {item.requires_review && (
            <div className="bg-[#F59E0B]/10 text-[#92400E] text-sm p-3 rounded-md flex gap-2" data-testid="content-requires-review-banner">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>No hay documentos de la base de conocimiento que cubran esta jurisdicción/código postal.
              El contenido deja los datos técnicos en blanco y requiere revisión manual antes de programarse.</span>
            </div>
          )}

          {item.duplication_warning && (
            <div className="bg-[#F59E0B]/10 text-[#92400E] text-sm p-3 rounded-md" data-testid="content-duplication-warning">
              {item.duplication_warning}
            </div>
          )}

          <div className="flex flex-wrap gap-2 text-xs text-[#71717A]">
            <Badge variant="outline" className="rounded-sm">{item.content_type}</Badge>
            <Badge variant="outline" className="rounded-sm">{item.audience}</Badge>
            {item.location && <Badge variant="outline" className="rounded-sm">{item.location}{item.state ? `, ${item.state}` : ""}</Badge>}
            <Badge variant="outline" className="rounded-sm">costo total: ${totalCost.toFixed(4)}</Badge>
          </div>

          {item.body && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Contenido (en inglés)</h4>
              <div
                className="prose prose-sm max-w-none border border-[#E4E4E7] rounded-md p-4 max-h-72 overflow-y-auto bg-[#FAFAFA]"
                data-testid="content-body-preview"
                dangerouslySetInnerHTML={{ __html: item.body }}
              />
            </div>
          )}

          {item.seo_title && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-[#71717A]">SEO title:</span> {item.seo_title}</div>
              <div><span className="text-[#71717A]">Slug:</span> {item.slug}</div>
              <div className="col-span-2"><span className="text-[#71717A]">Meta description:</span> {item.meta_description}</div>
            </div>
          )}

          {item.content_type === "gmb_post" && (
            <div className="space-y-1.5">
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium">CTA de la ficha de Google</h4>
              <Select value={item.gmb_cta || ""} onValueChange={setGmbCta} disabled={busy === "gmb-cta"}>
                <SelectTrigger data-testid="content-gmb-cta-select" className="h-8 text-xs w-48"><SelectValue placeholder="Elegir CTA" /></SelectTrigger>
                <SelectContent>
                  {GMB_CTA_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {(item.technical_citations || []).length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Citas técnicas</h4>
              <div className="space-y-2">
                {item.technical_citations.map((c, i) => (
                  <div key={i} className="text-xs border border-[#E4E4E7] rounded-md p-2.5" data-testid={`content-citation-${i}`}>
                    <p className="font-medium">{c.claim}</p>
                    <p className="text-[#71717A] mt-1">Fuente: {c.document_title} (fragmento #{c.chunk_index})</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(item.social_variants || []).length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Variantes por red social</h4>
              <div className="space-y-2">
                {item.social_variants.map((v, i) => (
                  <div key={i} className="text-xs border border-[#E4E4E7] rounded-md p-2.5" data-testid={`content-social-variant-${v.platform}`}>
                    <p className="font-medium uppercase text-[10px] text-[#0055FF]">{v.platform}</p>
                    <p className="mt-1 whitespace-pre-wrap">{v.text}</p>
                    {v.hashtags?.length > 0 && <p className="text-[#71717A] mt-1">{v.hashtags.map((h) => `#${h}`).join(" ")}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(item.review_checks || []).length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Revisión</h4>
              <div className="space-y-2">
                {item.review_checks.map((c, i) => {
                  const Icon = CHECK_ICON[c.status];
                  return (
                    <div key={i} className="flex gap-2 text-xs" data-testid={`content-check-${c.check_type}`}>
                      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${CHECK_COLOR[c.status]}`} />
                      <div><span className="font-medium">{CHECK_LABEL[c.check_type]}:</span> {c.message}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Imagen para publicar</h4>
            {images.length === 0 ? (
              <p className="text-xs text-[#71717A]">
                Todavía no generaste imágenes. <Link to="/dashboard/creative-studio" className="text-[#0055FF] underline" data-testid="content-goto-creative-studio">Ir al Estudio Creativo</Link>
              </p>
            ) : (
              <>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="content-image-picker">
                  {images.map((img) => (
                    <button
                      key={img.id} type="button" onClick={() => attachImage(img.id)} disabled={busy === "attach-image"}
                      className={`rounded-sm overflow-hidden border-2 ${item.generated_image_id === img.id ? "border-[#0055FF]" : "border-transparent"}`}
                      data-testid={`content-image-option-${img.id}`}
                    >
                      <img src={img.composed_url} alt="" className="aspect-square object-cover w-full" />
                    </button>
                  ))}
                </div>
                {item.generated_image_id && (
                  <Button size="sm" variant="ghost" className="text-[#EF4444] mt-1 px-0 text-xs h-6" disabled={busy === "attach-image"}
                    onClick={() => attachImage(null)} data-testid="content-remove-image-button">
                    <X className="h-3 w-3 mr-1" /> Quitar imagen
                  </Button>
                )}
              </>
            )}
          </div>

          {WORDPRESS_TYPES.includes(item.content_type) && (item.status === "approved" || item.wordpress_post_id) && (
            <div data-testid="content-wordpress-section">
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Publicar en WordPress</h4>
              {!wpConn || wpConn.connection_status !== "connected" ? (
                <p className="text-xs text-[#71717A]">
                  No hay una conexión de WordPress activa.{" "}
                  <Link to="/dashboard/connections" className="text-[#0055FF] underline" data-testid="content-goto-wordpress-connection">
                    Conectar en Conexiones
                  </Link>
                </p>
              ) : (
                <>
                  {item.wordpress_post_id && (
                    <div className="text-xs space-y-1 mb-2" data-testid="content-wordpress-published-info">
                      <p>
                        Estado en WordPress: <span className="font-medium">{item.wordpress_post_status}</span>
                        {" · "}
                        <a href={item.wordpress_post_url} target="_blank" rel="noreferrer" className="text-[#0055FF] underline" data-testid="content-wordpress-post-link">
                          Abrir el post
                        </a>
                      </p>
                      {item.wordpress_sync_state === "failed" && item.wordpress_last_error && (
                        <p className="text-[#EF4444]">{item.wordpress_last_error}</p>
                      )}
                    </div>
                  )}
                  {item.status === "approved" && (
                    <div className="space-y-2">
                      {item.requires_review && (
                        <p className="text-[10px] text-[#F59E0B]" data-testid="content-wordpress-requires-review-note">
                          Este contenido requiere revisión: siempre se guarda como borrador en WordPress, sin excepción.
                        </p>
                      )}
                      {!wpConn.can_publish && (
                        <p className="text-[10px] text-[#71717A]">
                          Tu usuario de WordPress solo puede dejar borradores (sin permiso de publicar directo).
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={wpStatus} onValueChange={setWpStatus} disabled={item.requires_review}>
                          <SelectTrigger data-testid="content-wordpress-status-select" className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Borrador</SelectItem>
                            <SelectItem value="publish">Publicar</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={wpCategoryId} onValueChange={setWpCategoryId}>
                          <SelectTrigger data-testid="content-wordpress-category-select" className="h-8 text-xs w-44"><SelectValue placeholder="Categoría" /></SelectTrigger>
                          <SelectContent>
                            {wpCategories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {wpConn.can_create_categories && (
                        <div className="flex items-center gap-2">
                          <Input
                            value={wpNewCategoryName} onChange={(e) => setWpNewCategoryName(e.target.value)}
                            placeholder="Crear categoría nueva…" className="h-8 text-xs w-44"
                            data-testid="content-wordpress-new-category-input"
                          />
                          <Button
                            size="sm" variant="outline" className="h-8 rounded-md text-xs" disabled={wpCreatingCategory || !wpNewCategoryName.trim()}
                            onClick={createWpCategory} data-testid="content-wordpress-create-category-button"
                          >
                            {wpCreatingCategory ? <Loader2 className="h-3 w-3 animate-spin" /> : "Crear"}
                          </Button>
                        </div>
                      )}
                      <Input
                        value={wpTagsCsv} onChange={(e) => setWpTagsCsv(e.target.value)}
                        placeholder="Etiquetas, separadas por coma" className="h-8 text-xs w-72"
                        data-testid="content-wordpress-tags-input"
                      />
                      <div>
                        <Button
                          size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" disabled={busy === "wordpress"}
                          onClick={publishToWordPress} data-testid="content-wordpress-publish-button"
                        >
                          {busy === "wordpress" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1" />}
                          Publicar en WordPress
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {PUSHABLE_STATUSES.includes(item.status) && (() => {
            const pushableAccounts = item.content_type === "gmb_post"
              ? accounts.filter((a) => a.platform === "google_business")
              : accounts.filter((a) => a.platform !== "google_business");
            const gmbBlocked = item.content_type === "gmb_post" && !item.gmb_cta;
            const pinterestSelected = pushAccountIds.some((id) => pushableAccounts.find((a) => a.id === id)?.platform === "pinterest");
            const pinterestBlocked = pinterestSelected && !item.generated_image_id;
            return (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Enviar a publicaciones</h4>
              {pushableAccounts.length === 0 ? (
                <p className="text-xs text-[#71717A]">
                  {item.content_type === "gmb_post" ? "No hay ninguna ficha de Business Profile conectada." : "No hay cuentas sociales conectadas."}
                </p>
              ) : (
                <div className="space-y-1" data-testid="content-push-accounts">
                  {pushableAccounts.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`content-push-account-${a.id}`}>
                      <Checkbox checked={pushAccountIds.includes(a.id)} onCheckedChange={() => togglePushAccount(a.id)}
                        disabled={a.connection_status !== "connected" || (a.platform === "pinterest" && !a.pinterest_board_id)} />
                      {a.display_name} <span className="text-[#A1A1AA] uppercase text-[10px]">{a.platform}</span>
                      {a.platform === "nextdoor" && (
                        <span
                          className="text-[10px] uppercase tracking-wider bg-[#8ED500]/15 text-[#4D7A00] px-1.5 py-0.5 rounded-sm cursor-help"
                          title="Nextdoor no permite que otras herramientas publiquen automáticamente. La plataforma te deja el post listo y vos lo publicás con un toque."
                        >
                          Asistida
                        </span>
                      )}
                      {a.connection_status !== "connected" && <span className="text-[#EF4444] text-[10px]">desconectada</span>}
                      {a.platform === "pinterest" && !a.pinterest_board_id && <span className="text-[#EF4444] text-[10px]">sin tablero elegido</span>}
                    </label>
                  ))}
                </div>
              )}
              {gmbBlocked && (
                <p className="text-[10px] text-[#F59E0B] mt-1" data-testid="content-gmb-cta-required-warning">
                  Elegí un CTA para la ficha de Google antes de enviarla.
                </p>
              )}
              {pinterestBlocked && (
                <p className="text-[10px] text-[#F59E0B] mt-1" data-testid="content-pinterest-image-required-warning">
                  Pinterest requiere una imagen asociada antes de enviarlo.
                </p>
              )}
              <Button size="sm" className="mt-2 bg-[#0055FF] hover:bg-[#0044CC] rounded-md" disabled={busy === "push" || gmbBlocked || pinterestBlocked}
                onClick={pushToPublications} data-testid="content-push-to-publications-button">
                {busy === "push" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5 mr-1" />}
                Enviar como borrador a Publicaciones
              </Button>
            </div>
            );
          })()}

          <Separator />

          <div className="flex flex-wrap gap-2" data-testid="content-actions">
            {item.body && !item.error_message && (
              <Button size="sm" variant="outline" className="rounded-md" disabled={busy === "expand"}
                onClick={() => run("expand", () => api.post(`/content/items/${item.id}/expand`))} data-testid="content-expand-button">
                {busy === "expand" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} Expandir
              </Button>
            )}
            {item.body && !item.error_message && (
              <Button size="sm" variant="outline" className="rounded-md" disabled={busy === "social"}
                onClick={() => run("social", () => api.post(`/content/items/${item.id}/social-variants`, { platforms: PLATFORMS }))}
                data-testid="content-social-variants-button">
                {busy === "social" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Share2 className="h-3.5 w-3.5 mr-1" />} Generar variantes sociales
              </Button>
            )}
            {item.status === "draft" && !item.error_message && (
              <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" disabled={busy === "review"}
                onClick={() => run("review", () => api.post(`/content/items/${item.id}/submit-review`))} data-testid="content-submit-review-button">
                {busy === "review" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Enviar a revisión
              </Button>
            )}
            {item.status === "pending_review" && (
              <Button size="sm" className="bg-[#10B981] hover:bg-[#0F9D6F] rounded-md" disabled={busy === "approve"}
                onClick={() => run("approve", () => api.post(`/content/items/${item.id}/approve`))} data-testid="content-approve-button">
                {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Aprobar
              </Button>
            )}
            {item.status === "approved" && (
              <div className="flex items-center gap-2">
                <Input type="datetime-local" className="h-8 text-xs w-44" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="content-schedule-input" />
                <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" disabled={busy === "schedule" || !scheduleAt}
                  onClick={() => run("schedule", () => api.post(`/content/items/${item.id}/schedule`, { scheduled_at: scheduleAt }))} data-testid="content-schedule-button">
                  {busy === "schedule" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5 mr-1" />} Programar
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
